import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { TOAST_ICON_TYPE } from '@/components/Elements';
import { API_STATUS } from '@/constants';
import { LANG_UPDATE_API_RESULT_CODE, useLangUpdateApi } from '@/features/api';
import { usePtidExpired } from '@/features/expired';
import { useLoginUserInfo } from '@/hooks/useLoginUserInfo';
import { useToastInfo } from '@/hooks/useToastInfo';

/**
 * 利用言語同期 カスタムフック
 */
export const useLangSynchronize = () => {
  const { fetchLangUpdate, langUpdateState } = useLangUpdateApi();
  const { ptidExpired } = usePtidExpired();
  const { addToastMessage } = useToastInfo();
  const { isSerialPlan } = useLoginUserInfo();
  const { t } = useTranslation();

  /**
   * 利用言語同期
   */
  const synchronizeLang = useCallback(
    (uiLang: string) => {
      // 利用中のユーザ種別チェック
      if (isSerialPlan) {
        // シリアルで利用中の場合は何もしない
        return;
      }

      // 利用言語更新APIにリクエスト送信
      fetchLangUpdate({ lang: uiLang });
    },
    [fetchLangUpdate, isSerialPlan],
  );

  /**
   * エラーダイアログ or エラーメッセージ表示
   */
  const showErrorDialogOrMessage = useCallback(() => {
    if (
      langUpdateState.data?.resultCode ===
      LANG_UPDATE_API_RESULT_CODE.INFO_EXPIRED_IDTOKEN
    ) {
      // PTIDエラー処理
      ptidExpired();

      return;
    }

    if (
      langUpdateState.data?.resultCode === LANG_UPDATE_API_RESULT_CODE.WARN_AUTH
    ) {
      // アクセス期限切れダイアログを表示
      return;
    }

    // 利用言語同期失敗エラーメッセージを表示
    addToastMessage(
      t('langSynchronized.利用言語の同期に失敗しました'),
      TOAST_ICON_TYPE.WARNING,
    );
  }, [addToastMessage, langUpdateState.data?.resultCode, ptidExpired, t]);

  /**
   * 利用言語更新APIリクエスト状態を監視
   */
  useEffect(() => {
    if (langUpdateState.status === API_STATUS.FAILED) {
      showErrorDialogOrMessage();
    }
  }, [langUpdateState.status, showErrorDialogOrMessage]);

  return {
    synchronizeLang,
    langUpdateState,
  };
};
